import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Select,
  InputNumber,
  message,
  Popconfirm,
  Tag,
  Icon,
  AutoComplete
} from 'antd';
import axios from 'axios';
import { BASE_URL } from '../../middleware/api';
import './TargetManagementPage.css';

const { Option } = Select;

const REGISTERED_OPTIONS = [
  { label: 'yes', value: true },
  { label: 'no', value: false },
  { label: 'any', value: null }
];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(
    2,
    '0'
  )}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const calcPercent = (current, planned) => {
  if (!planned || planned === 0) return '0%';
  return `${Math.round((current / planned) * 100)}%`;
};

const sortTargets = (targets) => {
  const active = targets.filter((t) => !t.removedAt);
  const inactive = targets.filter((t) => !!t.removedAt);
  active.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  inactive.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  return [...active, ...inactive];
};

const getSortedOptions = (options, order, labelKey) => {
  if (!order) return options;
  return [...options].sort((a, b) => {
    const aVal = labelKey ? a[labelKey] : a;
    const bVal = labelKey ? b[labelKey] : b;
    return order === 'asc'
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });
};

const FilterDropdownContent = ({
  draft,
  setDraft,
  filter,
  setFilter,
  options,
  labelKey,
  valueKey,
  onConfirm,
  confirm
}) => {
  const [selectOpen, setSelectOpen] = React.useState(false);

  return (
    <div style={{ padding: 8 }} onMouseDown={(e) => e.stopPropagation()}>
      <div style={{ position: 'relative' }}>
        <Select
          value={draft.value}
          mode='multiple'
          style={{ width: 165, marginBottom: 8, display: 'block' }}
          showSearch
          open={selectOpen}
          onDropdownVisibleChange={(open) => setSelectOpen(open)}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().startsWith(input.toLowerCase())
          }
          onChange={(val) => setDraft((d) => ({ ...d, value: val }))}
          allowClear
          placeholder='Search...'
          getPopupContainer={(trigger) => trigger.parentNode}
          dropdownClassName='filter-dropdown-select'
        >
          {getSortedOptions(options, draft.order, labelKey).map((opt) => {
            const v = valueKey ? opt[valueKey] : opt;
            const l = labelKey ? opt[labelKey] : opt;
            return (
              <Option key={v} value={v}>
                {l}
              </Option>
            );
          })}
        </Select>

        {selectOpen && (
          <div
            style={{
              position: 'absolute',
              bottom: '-30px',
              width: '100%',
              right: 0,
              fontSize: 13,
              color: '#1890ff',
              borderTop: '1px solid #e8e8e8',
              borderLeft: '1px solid #e8e8e8',
              borderRight: '1px solid #e8e8e8',
              textAlign: 'right',
              cursor: 'pointer',
              userSelect: 'none',
              zIndex: 1052,
              background: '#fff',
              padding: '4px 10px'
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setSelectOpen(false);
            }}
          >
            <Icon type='close' />
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <Button
          type='primary'
          size='small'
          onClick={() => {
            const applied = { ...draft };
            setFilter(applied);
            onConfirm(applied);
            confirm();
          }}
        >
          Filter
        </Button>
        <Button
          size='small'
          onClick={() => {
            const empty = { value: [], order: '' };
            setDraft(empty);
            setFilter(empty);
            onConfirm(empty);
            confirm();
          }}
        >
          Reset
        </Button>
      </div>
    </div>
  );
};

const DEFAULT_PAGE_SIZE = 10000;

const TargetManagementPage = () => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupNames, setGroupNames] = useState([]);
  const [agencyList, setAgencyList] = useState([]);
  const [countriesList, setCountriesList] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0
  });

  const [groupFilter, setGroupFilter] = useState({ value: [], order: '' });
  const [countryFilter, setCountryFilter] = useState({ value: [], order: '' });
  const [agencyFilter, setAgencyFilter] = useState({ value: [], order: '' });

  const [groupDraft, setGroupDraft] = useState({ value: [], order: '' });
  const [countryDraft, setCountryDraft] = useState({ value: [], order: '' });
  const [agencyDraft, setAgencyDraft] = useState({ value: [], order: '' });

  // sort state: { field: 'createdAt' | 'updatedAt', order: 'asc' | 'desc' | null }
  const [sortState, setSortState] = useState({ field: null, order: null });

  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [addForm, setAddForm] = useState({
    name: null,
    country: null,
    agency: null,
    maxDifference: 0,
    plannedDrafts: 0,
    registered: null
  });
  const [editForm, setEditForm] = useState({
    name: null,
    maxDifference: 0,
    plannedDrafts: 0
  });

  const hasActiveFilters =
    groupFilter.value.length > 0 ||
    countryFilter.value.length > 0 ||
    agencyFilter.value.length > 0;

  const [globalTotals, setGlobalTotals] = useState({
    planned: 0,
    current: 0,
    withinTarget: 0
  });

  const totalPlanned = globalTotals.planned;
  const totalCurrent = globalTotals.current;
  const totalWithinTarget = globalTotals.withinTarget;
  const totalWithinTargetPercent =
    totalPlanned > 0
      ? `${Math.round((totalWithinTarget / totalPlanned) * 100)}%`
      : '0%';
  const totalPercent =
    totalPlanned > 0
      ? `${Math.round((totalCurrent / totalPlanned) * 100)}%`
      : '0%';

  const loadGlobalTotals = async (params = {}) => {
    try {
      const query = new URLSearchParams();
      if (params.names && params.names.length)
        params.names.forEach((n) => query.append('names', n));
      if (params.countries && params.countries.length)
        params.countries.forEach((c) => query.append('countries', c));
      if (params.agencies && params.agencies.length)
        params.agencies.forEach((a) => query.append('agencies', a));
      query.append('page', 0);
      query.append('size', 10000);
      const res = await axios.get(`${BASE_URL}/target?${query.toString()}`);
      const all = res.data?.content || res.data || [];
      const active = all.filter((r) => !r.removedAt);
      const planned = active.reduce(
        (sum, r) => sum + (r.plannedDrafts || 0),
        0
      );
      const current = active.reduce(
        (sum, r) => sum + (r.currentDrafts || 0),
        0
      );
      const withinTarget = active.reduce((sum, r) => {
        const p = r.plannedDrafts || 0;
        const c = r.currentDrafts || 0;
        return sum + (c <= p ? c : p);
      }, 0);
      setGlobalTotals({ planned, current, withinTarget });
    } catch (e) {
      console.error('Failed to load global totals:', e);
    }
  };

  const loadTargets = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const query = new URLSearchParams();

      if (params.names && params.names.length)
        params.names.forEach((n) => query.append('names', n));
      if (params.countries && params.countries.length)
        params.countries.forEach((c) => query.append('countries', c));
      if (params.agencies && params.agencies.length)
        params.agencies.forEach((a) => query.append('agencies', a));

      const page = params.page !== undefined ? params.page : 0;
      const size = params.size || DEFAULT_PAGE_SIZE;
      query.append('page', page);
      query.append('size', size);

      if (params.sort) {
        query.append('sort', params.sort);
      }

      const url = `${BASE_URL}/target?${query.toString()}`;
      const response = await axios.get(url);
      const data = response.data;

      const raw = data.content || data || [];
      const content = raw.map((r) => ({
        ...r,
        percent:
          r.plannedDrafts > 0
            ? Math.round((r.currentDrafts / r.plannedDrafts) * 100)
            : 0
      }));
      const total = data.totalElements ?? content.length;
      const pageNumber = data.number ?? page;
      const pageSize = data.size ?? size;

      setTargets(params.sort ? content : sortTargets(content));
      setPagination((prev) => ({
        ...prev,
        current: pageNumber + 1,
        pageSize,
        total
      }));
    } catch (err) {
      console.error('Failed to load targets:', err);
      message.error('Failed to load targets.');
    } finally {
      setLoading(false);
    }
  }, []);

  const applyFilters = (
    gf = groupFilter,
    cf = countryFilter,
    af = agencyFilter,
    page = 0,
    size = pagination.pageSize,
    sort = sortState,
    withTotals = false
  ) => {
    const sortParam =
      sort.apiField && sort.order
        ? `${sort.apiField},${sort.order}`
        : sort.field && sort.order
        ? `${sort.field},${sort.order}`
        : undefined;
    const filterParams = {
      names: gf.value,
      countries: cf.value,
      agencies: af.value
    };

    console.log('gf.value', gf.value);

    loadTargets({ ...filterParams, page, size, sort: sortParam });
    if (withTotals) {
      loadGlobalTotals(filterParams);
    }
  };

  const handleResetFilters = () => {
    const empty = { value: [], order: '' };
    setGroupFilter(empty);
    setGroupDraft(empty);
    setCountryFilter(empty);
    setCountryDraft(empty);
    setAgencyFilter(empty);
    setAgencyDraft(empty);
    setSortState({ field: null, order: null });
    loadTargets({ page: 0, size: pagination.pageSize });
    loadGlobalTotals({});
  };

  const handleTableChange = (pag, _filters, sorter) => {
    const page = pag.current - 1;
    const size = pag.pageSize;
    setPagination((prev) => ({
      ...prev,
      current: pag.current,
      pageSize: size
    }));

    if (sorter && sorter.field) {
      const fieldMap = {
        percent: 'currentDraftsPercentage'
      };
      const newSort = sorter.order
        ? {
            field: sorter.field,
            apiField: fieldMap[sorter.field] || sorter.field,
            order: sorter.order === 'ascend' ? 'asc' : 'desc'
          }
        : { field: null, apiField: null, order: null };
      setSortState(newSort);
      applyFilters(
        groupFilter,
        countryFilter,
        agencyFilter,
        page,
        size,
        newSort,
        false
      );
    } else {
      applyFilters(
        groupFilter,
        countryFilter,
        agencyFilter,
        page,
        size,
        sortState,
        false
      );
    }
  };

  useEffect(() => {
    loadTargets({ page: 0, size: DEFAULT_PAGE_SIZE });
    loadGlobalTotals({});
    (async () => {
      try {
        const res = await axios.get(`${BASE_URL}/target/names`);
        setGroupNames(res.data || []);
      } catch (e) {
        console.error(e);
      }
      try {
        const res = await axios.get(`${BASE_URL}/assessment_image/agency_list`);
        const data = res.data?.body || res.data || [];
        setAgencyList(Array.isArray(data) ? data : Object.keys(data));
      } catch (e) {
        console.error(e);
      }
      try {
        const res = await axios.get(
          `${BASE_URL}/assessment_domain/countries_list`
        );
        setCountriesList(Array.isArray(res.data) ? res.data : [res.data]);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const makeFilterDropdown =
    ({
      draft,
      setDraft,
      filter,
      setFilter,
      options,
      labelKey,
      valueKey,
      onConfirm
    }) =>
    ({ confirm }) =>
      (
        <FilterDropdownContent
          draft={draft}
          setDraft={setDraft}
          filter={filter}
          setFilter={setFilter}
          options={options}
          labelKey={labelKey}
          valueKey={valueKey}
          onConfirm={onConfirm}
          confirm={confirm}
        />
      );

  const checkDuplicate = (country, agency, registered) =>
    targets.some(
      (t) =>
        t.country === country &&
        t.agency === agency &&
        t.registered === registered &&
        !t.removedAt
    );

  const handleAddSubmit = async () => {
    const { country, agency, maxDifference, plannedDrafts, registered } =
      addForm;

    const name =
      addForm.name && !groupNames.includes(addForm.name)
        ? addForm.name.replace(/\s+$/, '')
        : addForm.name;

    if (!name || !country || !agency) {
      message.error('Group, Country and Agency are required.');
      return;
    }
    if (checkDuplicate(country, agency, registered)) {
      message.error('This target already exists.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BASE_URL}/target`, {
        name,
        country,
        agency,
        maxDifference: maxDifference || 0,
        plannedDrafts: plannedDrafts || 0,
        registered: registered === null ? undefined : registered
      });
      message.success('Target created successfully.');
      setAddModalVisible(false);
      setAddForm({
        name: null,
        country: null,
        agency: null,
        maxDifference: 0,
        plannedDrafts: 0,
        registered: null
      });
      applyFilters(
        groupFilter,
        countryFilter,
        agencyFilter,
        0,
        pagination.pageSize,
        sortState,
        true
      );
      const res = await axios.get(`${BASE_URL}/target/names`);
      setGroupNames(res.data || []);
    } catch (err) {
      message.error('Failed to create target.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!editForm.name) {
      message.error('Group is required.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${BASE_URL}/target/${editTarget.id}`, {
        name: editForm.name,
        maxDifference: editForm.maxDifference || 0,
        plannedDrafts: editForm.plannedDrafts || 0
      });
      message.success('Target updated successfully.');
      setEditModalVisible(false);
      setEditTarget(null);
      applyFilters(
        groupFilter,
        countryFilter,
        agencyFilter,
        pagination.current - 1,
        pagination.pageSize,
        sortState,
        true
      );
    } catch (err) {
      message.error('Failed to update target.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleRemove = async (record) => {
    const isRemoved = !record.removedAt;
    try {
      await axios.put(
        `${BASE_URL}/target/${record.id}/remove?isRemoved=${isRemoved}`
      );
      message.success(isRemoved ? 'Target stopped.' : 'Target started.');
      applyFilters(
        groupFilter,
        countryFilter,
        agencyFilter,
        pagination.current - 1,
        pagination.pageSize,
        sortState,
        true
      );
    } catch (err) {
      message.error('Failed to update target status.');
    }
  };

  const registeredLabel = (val) =>
    val === true ? 'yes' : val === false ? 'no' : 'any';

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: 'Group',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      filterDropdown: makeFilterDropdown({
        draft: groupDraft,
        setDraft: setGroupDraft,
        filter: groupFilter,
        setFilter: setGroupFilter,
        options: groupNames,
        onConfirm: (f) =>
          applyFilters(
            f,
            countryFilter,
            agencyFilter,
            0,
            pagination.pageSize,
            sortState,
            true
          )
      }),
      filterIcon: () => (
        <Icon
          type='filter'
          style={{ color: groupFilter.value.length ? '#1890ff' : undefined }}
        />
      ),
      filteredValue: groupFilter.value.length ? groupFilter.value : null
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 140,
      filterDropdown: makeFilterDropdown({
        draft: countryDraft,
        setDraft: setCountryDraft,
        filter: countryFilter,
        setFilter: setCountryFilter,
        options: countriesList,
        labelKey: 'fullName',
        valueKey: 'fullName',
        onConfirm: (f) =>
          applyFilters(
            groupFilter,
            f,
            agencyFilter,
            0,
            pagination.pageSize,
            sortState,
            true
          )
      }),
      filterIcon: () => (
        <Icon
          type='filter'
          style={{ color: countryFilter.value.length ? '#1890ff' : undefined }}
        />
      ),
      filteredValue: countryFilter.value.length ? countryFilter.value : null
    },
    {
      title: 'Agency',
      dataIndex: 'agency',
      key: 'agency',
      width: 110,
      filterDropdown: makeFilterDropdown({
        draft: agencyDraft,
        setDraft: setAgencyDraft,
        filter: agencyFilter,
        setFilter: setAgencyFilter,
        options: agencyList,
        onConfirm: (f) =>
          applyFilters(
            groupFilter,
            countryFilter,
            f,
            0,
            pagination.pageSize,
            sortState,
            true
          )
      }),
      filterIcon: () => (
        <Icon
          type='filter'
          style={{ color: agencyFilter.value.length ? '#1890ff' : undefined }}
        />
      ),
      filteredValue: agencyFilter.value.length ? agencyFilter.value : null
    },
    {
      title: 'Max Diff',
      dataIndex: 'maxDifference',
      key: 'maxDifference',
      width: 90,
      align: 'center'
    },
    {
      title: 'Planned',
      dataIndex: 'plannedDrafts',
      key: 'plannedDrafts',
      width: 80,
      align: 'center',
      sorter: true,
      sortOrder:
        sortState.field === 'plannedDrafts'
          ? sortState.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null
    },
    {
      title: 'Current',
      dataIndex: 'currentDrafts',
      key: 'currentDrafts',
      width: 80,
      align: 'center',
      sorter: true,
      sortOrder:
        sortState.field === 'currentDrafts'
          ? sortState.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null
    },
    {
      title: '%',
      dataIndex: 'percent',
      key: 'percent',
      width: 70,
      align: 'center',
      sorter: true,
      sortOrder:
        sortState.field === 'percent'
          ? sortState.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null,
      render: (_, r) => calcPercent(r.currentDrafts, r.plannedDrafts)
    },
    {
      title: 'Registered',
      dataIndex: 'registered',
      key: 'registered',
      width: 100,
      align: 'center',
      render: (val) => registeredLabel(val)
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (val) => formatDate(val),
      sorter: true,
      sortOrder:
        sortState.field === 'createdAt'
          ? sortState.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 140,
      render: (val) => formatDate(val),
      sorter: true,
      sortOrder:
        sortState.field === 'updatedAt'
          ? sortState.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null
    },
    {
      title: 'By',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      width: 100,
      render: (val, r) => val || r.createdBy || '—'
    },
    {
      title: 'Status',
      key: 'status',
      width: 90,
      align: 'center',
      render: (_, r) =>
        r.removedAt ? (
          <Tag color='red'>Stopped</Tag>
        ) : (
          <Tag color='green'>Active</Tag>
        )
    },
    {
      title: 'Action',
      key: 'action',
      width: 90,
      align: 'center',
      render: (_, r) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
          <Button
            size='small'
            icon='edit'
            onClick={() => {
              setEditTarget(r);
              setEditForm({
                name: r.name,
                maxDifference: r.maxDifference,
                plannedDrafts: r.plannedDrafts
              });
              setEditModalVisible(true);
            }}
          />
          <Popconfirm
            title={r.removedAt ? 'Start this target?' : 'Stop this target?'}
            onConfirm={() => handleToggleRemove(r)}
            okText='Yes'
            cancelText='No'
          >
            <Button
              size='small'
              icon={r.removedAt ? 'play-circle' : 'pause-circle'}
              type={r.removedAt ? 'default' : 'danger'}
            />
          </Popconfirm>
        </div>
      )
    }
  ];

  return (
    <div className='target-management-page'>
      <div className='target-management-page__header'>
        <h2 className='target-management-page__title'>Target Management</h2>

        {/* Totals bar — centered in header */}
        <div className='target-totals-bar'>
          <div className='target-totals-bar__item'>
            <span className='target-totals-bar__label'>Planned</span>
            <span className='target-totals-bar__value'>{totalPlanned}</span>
          </div>
          <div className='target-totals-bar__divider' />
          <div className='target-totals-bar__item'>
            <span className='target-totals-bar__label'>Current</span>
            <span className='target-totals-bar__value'>{totalCurrent}</span>
          </div>
          <div className='target-totals-bar__divider' />
          <div className='target-totals-bar__item'>
            <span className='target-totals-bar__label'>%</span>
            <span className='target-totals-bar__value'>{totalPercent}</span>
          </div>
          <div className='target-totals-bar__divider' />
          <div className='target-totals-bar__item'>
            <span className='target-totals-bar__label'>Within target</span>
            <span className='target-totals-bar__value'>
              {totalWithinTarget}
            </span>
          </div>
          <div className='target-totals-bar__divider' />
          <div className='target-totals-bar__item'>
            <span className='target-totals-bar__label'>% Within target</span>
            <span className='target-totals-bar__value'>
              {totalWithinTargetPercent}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {hasActiveFilters && (
            <Button onClick={handleResetFilters} icon='close-circle'>
              Reset filters
            </Button>
          )}
          <Button type='primary' onClick={() => setAddModalVisible(true)}>
            Add New Target
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={targets}
        loading={loading}
        rowKey='id'
        size='middle'
        bordered
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`
        }}
        onChange={handleTableChange}
        rowClassName={(r) => (r.removedAt ? 'target-row--inactive' : '')}
        scroll={{ x: 1100 }}
      />

      {/* Add Modal */}
      <Modal
        title='Add New Target'
        visible={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        footer={[
          <Button key='cancel' onClick={() => setAddModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key='save'
            type='primary'
            loading={submitting}
            onClick={handleAddSubmit}
          >
            Save
          </Button>
        ]}
        width={500}
      >
        <Form layout='vertical'>
          <Form.Item
            label={
              <span>
                GROUP <span style={{ color: 'red' }}>*</span>
              </span>
            }
          >
            {/* <Select
              placeholder='-- Select Target --'
              value={addForm.name}
              onChange={(val) => setAddForm((f) => ({ ...f, name: val }))}
              showSearch
              allowClear
            >
              {groupNames.map((n) => (
                <Option key={n} value={n}>
                  {n}
                </Option>
              ))}
            </Select> */}
            <AutoComplete
              placeholder='Select or type target'
              value={addForm.name}
              onChange={(val) => setAddForm((f) => ({ ...f, name: val }))}
              dataSource={groupNames}
              filterOption={(input, option) =>
                option.props.children
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              allowClear
            />
          </Form.Item>
          <Form.Item
            label={
              <span>
                COUNTRY <span style={{ color: 'red' }}>*</span>
              </span>
            }
          >
            <Select
              placeholder='-- Select Country --'
              value={addForm.country}
              onChange={(val) => setAddForm((f) => ({ ...f, country: val }))}
              showSearch
              allowClear
            >
              {countriesList.map((c) => (
                <Option
                  key={c.alpha3Code || c}
                  value={c.fullName || c.alpha3Code || c}
                >
                  {c.fullName || c.alpha3Code || c}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={
              <span>
                AGENCY <span style={{ color: 'red' }}>*</span>
              </span>
            }
          >
            <Select
              placeholder='-- Select Agency --'
              value={addForm.agency}
              onChange={(val) => setAddForm((f) => ({ ...f, agency: val }))}
              showSearch
              allowClear
            >
              {agencyList.map((a) => (
                <Option key={a} value={a}>
                  {a}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label='MAX DIFFERENCE'>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={addForm.maxDifference}
              onChange={(val) =>
                setAddForm((f) => ({ ...f, maxDifference: val }))
              }
            />
          </Form.Item>
          <Form.Item label='PLANNED DRAFTS'>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={addForm.plannedDrafts}
              onChange={(val) =>
                setAddForm((f) => ({ ...f, plannedDrafts: val }))
              }
            />
          </Form.Item>
          <Form.Item label='REGISTERED'>
            <Select
              placeholder='-- Select --'
              value={
                addForm.registered === null
                  ? 'any'
                  : addForm.registered
                  ? 'yes'
                  : 'no'
              }
              onChange={(val) =>
                setAddForm((f) => ({
                  ...f,
                  registered: val === 'yes' ? true : val === 'no' ? false : null
                }))
              }
            >
              {REGISTERED_OPTIONS.map((o) => (
                <Option key={o.label} value={o.label}>
                  {o.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title='Edit Target'
        visible={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={[
          <Button key='cancel' onClick={() => setEditModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key='save'
            type='primary'
            loading={submitting}
            onClick={handleEditSubmit}
          >
            Save
          </Button>
        ]}
        width={500}
      >
        <Form layout='vertical'>
          <Form.Item
            label={
              <span>
                GROUP <span style={{ color: 'red' }}>*</span>
              </span>
            }
          >
            <Select
              placeholder='-- Select Target --'
              value={editForm.name}
              onChange={(val) => setEditForm((f) => ({ ...f, name: val }))}
              showSearch
              allowClear
            >
              {groupNames.map((n) => (
                <Option key={n} value={n}>
                  {n}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label='COUNTRY'>
            <input
              className='ant-input'
              value={editTarget?.country || ''}
              disabled
            />
          </Form.Item>
          <Form.Item label='AGENCY'>
            <input
              className='ant-input'
              value={editTarget?.agency || ''}
              disabled
            />
          </Form.Item>
          <Form.Item label='MAX DIFFERENCE'>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={editForm.maxDifference}
              onChange={(val) =>
                setEditForm((f) => ({ ...f, maxDifference: val }))
              }
            />
          </Form.Item>
          <Form.Item label='PLANNED DRAFTS'>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={editForm.plannedDrafts}
              onChange={(val) =>
                setEditForm((f) => ({ ...f, plannedDrafts: val }))
              }
            />
          </Form.Item>
          <Form.Item label='REGISTERED'>
            <input
              className='ant-input'
              value={registeredLabel(editTarget?.registered)}
              disabled
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TargetManagementPage;
